import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getMerchantReadiness } from '../api/endpoints/merchant-readiness';
import {
  cancelSession,
  ClassSessionRecordSchema,
  createSession,
  listSessions,
  updateSession,
  type ClassSessionRecord,
  type CreateSessionInput,
} from '../api/endpoints/sessions';
import type { BusinessDetail } from '../api/schemas';
import { classSessionInput, MAX_CANCELLATION_HOURS, type ClassDraft, type ClassPaymentMode } from '../lib/class-session-form';
import { centsToDollarsInput } from '../lib/money';
import { offerDateInput } from '../lib/offer-form';
import { formatDateTime, formatMoneyCents } from '../lib/format';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { useVersionConflict } from '../lib/use-version-conflict';
import { isVersionConflict } from '../lib/version-conflict';
import { ErrorBanner } from './ErrorBanner';
import { FormField } from './FormField';

/** The API caps a listing window at 31 days; a month ahead is what a studio plans. */
const WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60_000;
const SESSIONS_QUERY_KEY = ['class-sessions'];

const EMPTY_DRAFT: ClassDraft = {
  serviceId: '', instructorId: '', startsAt: '', durationMin: '', capacity: '10', priceInput: '',
  cutoffHours: '0', paymentMode: 'pay_at_studio',
};
const PROVIDERS = ['yappy', 'paguelofacil'] as const;

type Props = { business: BusinessDetail; owner: boolean; resources?: readonly { id: string; name: string }[] };

/** What the class form holds, in the units the server compares: the start to the minute, the length in minutes. */
type SessionDraft = {
  serviceId: string;
  instructorId: string;
  startsAt: string;
  durationMin: number;
  capacity: number;
  priceCents: number;
  cutoffMin: number;
  paymentMode: ClassPaymentMode;
  resourceIds: string[];
};
const SESSION_DRAFT_KEYS = [
  'serviceId', 'instructorId', 'startsAt', 'durationMin', 'capacity', 'priceCents', 'cutoffMin', 'paymentMode', 'resourceIds',
] as const;
/** One save of the form: a new class, or an edit naming the version it was read at. */
type SaveInput = { input: CreateSessionInput; sessionId?: string; expectedVersion?: number };

/** The form shows minutes, so a start with seconds compares at the minute. */
function toTheMinute(iso: string): string {
  return new Date(offerDateInput(iso)).toISOString();
}

function sessionDraft(entry: ClassSessionRecord): SessionDraft {
  return {
    serviceId: entry.serviceId,
    instructorId: entry.instructorId,
    startsAt: toTheMinute(entry.startsAt),
    durationMin: Math.round((Date.parse(entry.endsAt) - Date.parse(entry.startsAt)) / MINUTE_MS),
    capacity: entry.capacity,
    priceCents: entry.priceCents,
    cutoffMin: entry.policy?.cancellationCutoffMin ?? 0,
    paymentMode: entry.policy?.paymentMode ?? 'pay_at_studio',
    resourceIds: [...entry.resourceIds].sort(),
  };
}

/** The draft a request body stands for: what a save sent, in the units the server compares. */
function draftFromInput(input: CreateSessionInput): SessionDraft {
  return {
    serviceId: input.serviceId,
    instructorId: input.instructorId,
    startsAt: input.startsAt,
    durationMin: Math.round((Date.parse(input.endsAt) - Date.parse(input.startsAt)) / MINUTE_MS),
    capacity: input.capacity,
    priceCents: input.priceCents,
    cutoffMin: input.policy.cancellationCutoffMin,
    paymentMode: input.policy.paymentMode,
    resourceIds: [...input.resourceIds].sort(),
  };
}

function inputFromDraft(draft: SessionDraft): CreateSessionInput {
  return {
    serviceId: draft.serviceId,
    instructorId: draft.instructorId,
    startsAt: draft.startsAt,
    endsAt: new Date(Date.parse(draft.startsAt) + draft.durationMin * MINUTE_MS).toISOString(),
    capacity: draft.capacity,
    resourceIds: draft.resourceIds,
    priceCents: draft.priceCents,
    policy: { cancellationCutoffMin: draft.cutoffMin, paymentMode: draft.paymentMode },
  };
}

function parseSessionRecord(current: unknown): ClassSessionRecord | null {
  const parsed = ClassSessionRecordSchema.safeParse(current);
  return parsed.success ? parsed.data : null;
}

function cutoffText(minutes: number): string {
  return minutes === 0 ? 'Hasta que empiece' : `${minutes / 60} h antes`;
}

/** Publish upcoming classes so clients can book them; cancel the ones that won't happen. */
export function ClassScheduler({ business, owner, resources = [] }: Props) {
  const queryClient = useQueryClient();
  const editorRef = useRef<HTMLElement>(null);
  const window = useMemo(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    return { from: from.toISOString(), to: new Date(from.getTime() + WINDOW_DAYS * DAY_MS).toISOString() };
  }, []);
  const sessionsQuery = useQuery({
    queryKey: [...SESSIONS_QUERY_KEY, business.id],
    queryFn: () => listSessions(window.from, window.to),
  });

  // Same readiness the server checks: a provider that can both charge and take prepayments.
  const readiness = useQueries({
    queries: PROVIDERS.map((provider) => ({
      queryKey: ['merchant-readiness', provider],
      queryFn: () => getMerchantReadiness(provider),
      enabled: owner,
    })),
  });
  const prepayAvailable = readiness.some((query) => query.data?.checkout && query.data.prepay);

  const [draft, setDraft] = useState<ClassDraft>(EMPTY_DRAFT);
  const [editing, setEditing] = useState<ClassSessionRecord | null>(null);
  const [resourceIds, setResourceIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | undefined>();
  // Cancelling notifies everyone booked, so it takes a second, explicit tap.
  const [confirmingId, setConfirmingId] = useState<string | undefined>();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
  // What the form started from and its version, set when editing starts and after each save: a newer copy
  // arriving in the background never moves them, so a save always names the version this person saw.
  const start = useRef<{ draft: SessionDraft; version: number; record: ClassSessionRecord } | null>(null);

  function startFrom(record: ClassSessionRecord | null) {
    start.current = record ? { draft: sessionDraft(record), version: record.version, record } : null;
  }

  /** The saved class goes straight into the schedule: a read right after a write can come back old. */
  function keepSaved(record: ClassSessionRecord) {
    queryClient.setQueryData<{ data: ClassSessionRecord[] }>([...SESSIONS_QUERY_KEY, business.id], (schedule) =>
      schedule ? { ...schedule, data: schedule.data.map((entry) => (entry.id === record.id ? record : entry)) } : schedule,
    );
  }

  function seedForm(next: SessionDraft) {
    setDraft({
      serviceId: next.serviceId,
      instructorId: next.instructorId,
      startsAt: offerDateInput(next.startsAt),
      durationMin: String(next.durationMin),
      capacity: String(next.capacity),
      priceInput: centsToDollarsInput(next.priceCents),
      cutoffHours: String(next.cutoffMin / 60),
      paymentMode: next.paymentMode,
    });
    setResourceIds(next.resourceIds);
    setFormError(undefined);
  }

  /** What the form holds now; null (and the reason shown) when it does not validate. */
  function formDraft(): SessionDraft | null {
    const result = classSessionInput(draft, business.services, new Date(), prepayAvailable);
    if (!result.ok) {
      setFormError(result.message);
      return null;
    }
    return draftFromInput({ ...result.input, resourceIds });
  }

  const create = useMutationAttempt({
    mutationFn: ({ input, sessionId, expectedVersion }: SaveInput, key: string) =>
      sessionId
        ? updateSession(sessionId, { ...input, ...(expectedVersion !== undefined ? { expectedVersion } : {}) }, key)
        : createSession(input, key),
    exito: (variables) => ((variables as { sessionId?: string }).sessionId ? 'Cambios guardados' : 'Clase publicada'),
    onSuccess: (saved, input) => {
      setDraft((current) => ({ ...current, startsAt: '' }));
      startFrom(null);
      setEditing(null);
      setFormError(undefined);
      if (input.sessionId) keepSaved(saved);
      else invalidate();
    },
    onError: (error, input) => {
      if (input.sessionId && start.current) conflict.handle(error, { base: start.current.draft, mine: draftFromInput(input.input) });
    },
  });

  const conflict = useVersionConflict<ClassSessionRecord, SessionDraft>({
    keys: SESSION_DRAFT_KEYS,
    labels: {
      serviceId: 'Clase',
      instructorId: 'Instructor',
      startsAt: 'Fecha y hora',
      durationMin: 'Duración',
      capacity: 'Cupo',
      priceCents: 'Precio',
      cutoffMin: 'Cancelación gratis',
      paymentMode: 'Pago',
      resourceIds: 'Lugares de la clase',
    },
    format: {
      serviceId: (id) => business.services.find((entry) => entry.id === id)?.name ?? id,
      instructorId: (id) => business.staff.find((member) => member.id === id)?.name ?? id,
      startsAt: formatDateTime,
      durationMin: (minutes) => `${minutes} min`,
      priceCents: (cents) => formatMoneyCents(cents),
      cutoffMin: cutoffText,
      resourceIds: (ids) => (ids.length === 0 ? 'Vacío' : ids.map((id) => resources.find((place) => place.id === id)?.name ?? id).join(', ')),
    },
    parse: parseSessionRecord,
    toDraft: sessionDraft,
    rebase: ({ draft: merged, record }) => {
      seedForm(merged);
      setEditing(record);
      startFrom(record);
    },
    save: ({ draft: merged, version }) => {
      const saved = start.current?.record;
      if (saved) create.mutate({ input: inputFromDraft(merged), sessionId: saved.id, expectedVersion: version });
    },
    current: formDraft,
    discard: (record) => {
      keepSaved(record);
      seedForm(sessionDraft(record));
      setEditing(record);
      startFrom(record);
    },
  });
  const cancel = useMutationAttempt({
    mutationFn: (sessionId: string, key: string) => cancelSession(sessionId, key),
    onSuccess: () => {
      setConfirmingId(undefined);
      invalidate();
    },
  });

  const service = business.services.find((entry) => entry.id === draft.serviceId);
  const instructors = business.staff.filter(
    (member) => !draft.serviceId || !member.serviceIds || member.serviceIds.includes(draft.serviceId),
  );

  function pickService(serviceId: string) {
    const picked = business.services.find((entry) => entry.id === serviceId);
    const stillTeaches = (memberId: string) =>
      business.staff.some((m) => m.id === memberId && (!m.serviceIds || m.serviceIds.includes(serviceId)));
    setDraft((current) => ({
      ...current,
      serviceId,
      instructorId: stillTeaches(current.instructorId) ? current.instructorId : '',
      durationMin: picked ? String(picked.durationMin) : current.durationMin,
      priceInput: picked ? centsToDollarsInput(picked.priceCents) : current.priceInput,
    }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (create.uncertain) { create.retry(); return; }
    const result = classSessionInput(draft, business.services, new Date(), prepayAvailable);
    if (!result.ok) { setFormError(result.message); return; }
    setFormError(undefined);
    conflict.clear();
    const version = editing ? start.current?.version : undefined;
    create.mutate({
      input: { ...result.input, resourceIds },
      sessionId: editing?.id,
      ...(version !== undefined ? { expectedVersion: version } : {}),
    });
  }

  function edit(entry: ClassSessionRecord) {
    if (create.isPending || create.uncertain || conflict.asking) return;
    conflict.clear();
    startFrom(entry);
    setEditing(entry);
    setResourceIds([...entry.resourceIds]);
    setDraft({ serviceId: entry.serviceId, instructorId: entry.instructorId,
      startsAt: offerDateInput(entry.startsAt), durationMin: String((Date.parse(entry.endsAt) - Date.parse(entry.startsAt)) / MINUTE_MS),
      capacity: String(entry.capacity), priceInput: centsToDollarsInput(entry.priceCents),
      cutoffHours: String((entry.policy?.cancellationCutoffMin ?? 0) / 60),
      paymentMode: entry.policy?.paymentMode ?? 'pay_at_studio' });
    setFormError(undefined);
    editorRef.current?.scrollIntoView?.({ block: 'start' });
    editorRef.current?.focus({ preventScroll: true });
  }

  const upcoming = (sessionsQuery.data?.data ?? [])
    .filter((entry) => entry.status === 'published')
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  return (
    <>
      <section className="section" aria-label="Próximas clases">
        <h2 className="section-label">Próximas clases</h2>
        {sessionsQuery.isError ? <ErrorBanner message={messageFor(sessionsQuery.error)} /> : null}
        {cancel.isError ? <ErrorBanner message={messageFor(cancel.error)} /> : null}
        {sessionsQuery.data && upcoming.length === 0 ? (
          <p className="row-sub">Nada programado todavía. Publica la primera abajo.</p>
        ) : null}
        {upcoming.length > 0 ? (
          <ul className="rows">
            {upcoming.map((entry) => (
              <li key={entry.id} className="row">
                <div className="grow">
                  <p className="row-title">{nameOf(business, entry)}</p>
                  <p className="row-sub">
                    {formatDateTime(entry.startsAt)} · {instructorOf(business, entry)} · {cupoLabel(entry)} ·{' '}
                    {formatMoneyCents(entry.priceCents)}
                  </p>
                  <p className="row-sub">{policyLabel(entry)}</p>
                  {owner && Date.parse(entry.startsAt) > Date.now() && !create.isPending && !create.uncertain && !conflict.asking ? (
                    <button type="button" className="btn small secondary" onClick={() => edit(entry)}>Editar</button>
                  ) : null}
                  {owner && confirmingId === entry.id ? (
                    <div className="row-actions">
                      <p className="row-sub">{bookedLabel(entry)}</p>
                      <button type="button" className="btn small secondary" onClick={() => setConfirmingId(undefined)}>
                        No
                      </button>
                      <button
                        type="button"
                        className="btn small danger"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(entry.id)}
                      >
                        Sí, cancelar clase
                      </button>
                    </div>
                  ) : null}
                </div>
                {owner && confirmingId !== entry.id ? (
                  <button type="button" className="btn small secondary" onClick={() => setConfirmingId(entry.id)}>
                    Cancelar
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {owner ? (
        <section ref={editorRef} tabIndex={-1} id="class-editor" className="section" style={{ scrollMarginTop: 'calc(var(--space-7) + var(--space-5))' }}
          aria-label={editing ? 'Editar clase' : 'Programar clase'}>
          <h2 className="section-label">{editing ? 'Editar clase' : 'Programar clase'}</h2>
          <form className="card form-grid cols-2" onSubmit={handleSubmit}>
            <FormField label="Clase" htmlFor="clase-servicio">
              <select id="clase-servicio" value={draft.serviceId} onChange={(event) => pickService(event.target.value)} required>
                <option value="">Elige una</option>
                {business.services.map((entry) => (
                  <option key={entry.id} value={entry.id}>{entry.name}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Instructor" htmlFor="clase-instructor">
              <select
                id="clase-instructor"
                value={draft.instructorId}
                onChange={(event) => setDraft({ ...draft, instructorId: event.target.value })}
                required
              >
                <option value="">Elige quién</option>
                {instructors.map((member) => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Fecha y hora" htmlFor="clase-inicio">
              <input
                id="clase-inicio"
                type="datetime-local"
                value={draft.startsAt}
                onChange={(event) => setDraft({ ...draft, startsAt: event.target.value })}
                required
              />
            </FormField>
            <FormField label="Duración (minutos)" htmlFor="clase-duracion" hint={service ? `Tu ${service.name} dura ${service.durationMin}` : undefined}>
              <input
                id="clase-duracion"
                type="number"
                min={1}
                max={1440}
                value={draft.durationMin}
                onChange={(event) => setDraft({ ...draft, durationMin: event.target.value })}
                required
              />
            </FormField>
            <FormField label="Cupo" htmlFor="clase-cupo" hint="Personas por clase">
              <input
                id="clase-cupo"
                type="number"
                min={1}
                max={500}
                value={draft.capacity}
                onChange={(event) => setDraft({ ...draft, capacity: event.target.value })}
                required
              />
            </FormField>
            {resources.length > 0 ? <FormField label="Lugares de la clase" htmlFor="clase-lugares"
              hint="Sin selección, la clase no limita lugares específicos.">
              <select id="clase-lugares" multiple value={resourceIds} disabled={create.isPending || create.uncertain}
                onChange={(event) => setResourceIds(Array.from(event.target.selectedOptions, option => option.value))}>
                {resources.map(resource => <option key={resource.id} value={resource.id}>{resource.name}</option>)}
              </select>
            </FormField> : null}
            <FormField label="Precio" htmlFor="clase-precio" hint="Por persona, en dólares">
              <input
                id="clase-precio"
                inputMode="decimal"
                value={draft.priceInput}
                onChange={(event) => setDraft({ ...draft, priceInput: event.target.value })}
                required
              />
            </FormField>
            <FormField
              label="Cancelación gratis hasta (horas antes)"
              htmlFor="clase-cancelacion"
              hint="0 permite cancelar hasta que empiece"
            >
              <input
                id="clase-cancelacion"
                type="number"
                min={0}
                max={MAX_CANCELLATION_HOURS}
                step={1}
                value={draft.cutoffHours}
                onChange={(event) => setDraft({ ...draft, cutoffHours: event.target.value })}
                required
              />
            </FormField>
            <div className="span-2">
              {/* Same segmented control as Cobrar: the group names itself, the caption stays plain. */}
              <p className="field-label">Pago</p>
              <div className="seg" role="radiogroup" aria-label="Pago de la clase">
                <button
                  type="button"
                  aria-pressed={draft.paymentMode === 'pay_at_studio'}
                  onClick={() => setDraft({ ...draft, paymentMode: 'pay_at_studio' })}
                >
                  Paga en el estudio
                </button>
                <button
                  type="button"
                  aria-pressed={draft.paymentMode === 'prepay_required'}
                  disabled={!prepayAvailable}
                  onClick={() => setDraft({ ...draft, paymentMode: 'prepay_required' })}
                >
                  Pago anticipado obligatorio
                </button>
              </div>
              {!prepayAvailable ? (
                <p className="field-help">Para exigir pago anticipado, activa los cobros en línea en Negocio.</p>
              ) : null}
            </div>
            {formError ? <ErrorBanner message={formError} /> : null}
            {create.isError && !isVersionConflict(create.error) ? <ErrorBanner message={messageFor(create.error)} /> : null}
            {/* Where the person pressed Guardar: the choice appears in place of the buttons. */}
            {conflict.notice}
            {conflict.asking ? null : (
              <>
                <button
                  type={create.uncertain ? 'button' : 'submit'}
                  onClick={create.uncertain ? create.retry : undefined}
                  className="btn"
                  disabled={create.isPending}
                >
                  {create.isPending ? 'Guardando…' : create.uncertain ? 'Reintentar' : editing ? 'Guardar cambios' : 'Publicar clase'}
                </button>
                {editing ? <button type="button" className="btn secondary" disabled={create.isPending || create.uncertain}
                  onClick={() => { startFrom(null); setEditing(null); setDraft(EMPTY_DRAFT); setResourceIds([]); setFormError(undefined); }}>Dejar de editar</button> : null}
              </>
            )}
          </form>
        </section>
      ) : null}
    </>
  );
}

function nameOf(business: BusinessDetail, entry: ClassSessionRecord): string {
  return business.services.find((s) => s.id === entry.serviceId)?.name ?? entry.serviceId;
}

function instructorOf(business: BusinessDetail, entry: ClassSessionRecord): string {
  return business.staff.find((m) => m.id === entry.instructorId)?.name ?? entry.instructorId;
}

function policyLabel(entry: ClassSessionRecord): string {
  const policy = entry.policy ?? { cancellationCutoffMin: 0, paymentMode: 'pay_at_studio' as const };
  const hours = Math.round(policy.cancellationCutoffMin / 60);
  const cancel = hours === 0 ? 'Cancela hasta que empiece' : `Cancela hasta ${hours} h antes`;
  return `${cancel} · ${policy.paymentMode === 'prepay_required' ? 'Pago anticipado' : 'Paga en el estudio'}`;
}

function cupoLabel(entry: ClassSessionRecord): string {
  const taken = entry.capacity - entry.availableCapacity;
  const label = `${taken} de ${entry.capacity} cupos`;
  return entry.availableCapacity === 0 ? `Llena, ${label}` : label;
}

function bookedLabel(entry: ClassSessionRecord): string {
  const taken = entry.capacity - entry.availableCapacity;
  if (taken === 0) return 'Nadie reservado';
  return taken === 1 ? '1 persona reservada' : `${taken} personas reservadas`;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso. Intenta de nuevo.';
}
